import { useState } from "react";
import { Loader2, X, Mail, KeyRound, Server } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRESETS = [
  { id: "gmail", label: "Gmail", smtp: "smtp.gmail.com:587", imap: "imap.gmail.com:993" },
  {
    id: "outlook",
    label: "Outlook / Microsoft 365",
    smtp: "smtp-mail.outlook.com:587",
    imap: "outlook.office365.com:993",
  },
  { id: "yahoo", label: "Yahoo", smtp: "smtp.mail.yahoo.com:587", imap: "imap.mail.yahoo.com:993" },
  { id: "zoho", label: "Zoho", smtp: "smtp.zoho.com:587", imap: "imap.zoho.com:993" },
  { id: "custom", label: "Custom server", smtp: "your-host:587", imap: "your-host:993" },
];

export default function ConnectSMTPModal({ open, onClose, onSuccess }) {
  const [provider, setProvider] = useState("gmail");
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const isCustom = provider === "custom";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        email_address: emailAddress.trim(),
        password,
        name: name.trim() || null,
        provider_preset: provider,
      };
      if (isCustom) {
        if (!smtpHost || !smtpPort || !imapHost || !imapPort) {
          throw new Error("Please fill all custom server fields");
        }
        payload.smtp_host = smtpHost;
        payload.smtp_port = parseInt(smtpPort);
        payload.imap_host = imapHost;
        payload.imap_port = parseInt(imapPort);
      }
      await api.post("/inboxes", payload);
      toast.success(`${emailAddress} connected`);
      onSuccess?.();
      onClose();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Connection failed";
      toast.error(typeof msg === "string" ? msg : "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      data-testid="smtp-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg bg-[#121212] border border-[#27272a] rounded-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
              Connect via SMTP
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight mt-1">
              Add a mailbox without OAuth
            </h3>
            <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
              For Gmail, generate an App Password at{" "}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
                className="text-white hover:underline"
              >
                myaccount.google.com/apppasswords
              </a>{" "}
              (2-Step Verification must be on).
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="smtp-close"
            className="p-1.5 text-neutral-500 hover:bg-[#1a1a1a] rounded-md"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <Label icon={Server}>Provider</Label>
            <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {PRESETS.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setProvider(p.id)}
                  data-testid={`preset-${p.id}`}
                  className={`px-3 py-2 rounded-md text-xs border transition-colors text-left ${
                    provider === p.id
                      ? "bg-white text-black border-white"
                      : "bg-[#0d0d0d] text-neutral-300 border-[#27272a] hover:bg-[#1a1a1a]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label icon={Mail}>Email address</Label>
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="you@company.com"
              data-testid="smtp-email-input"
              required
              className="mt-1 w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>

          <div>
            <Label icon={KeyRound}>
              {provider === "gmail" ? "App password (16 chars)" : "Password"}
            </Label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="smtp-password-input"
              required
              className="mt-1 w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>

          <div>
            <Label>Display name (optional)</Label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane from Acme"
              data-testid="smtp-name-input"
              className="mt-1 w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm"
            />
          </div>

          {isCustom && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#1a1a1a]">
              <div>
                <Label>SMTP host</Label>
                <input
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="smtp.example.com"
                  className="mt-1 w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <Label>SMTP port</Label>
                <input
                  type="number"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  placeholder="587"
                  className="mt-1 w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <Label>IMAP host</Label>
                <input
                  value={imapHost}
                  onChange={(e) => setImapHost(e.target.value)}
                  placeholder="imap.example.com"
                  className="mt-1 w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <Label>IMAP port</Label>
                <input
                  type="number"
                  value={imapPort}
                  onChange={(e) => setImapPort(e.target.value)}
                  placeholder="993"
                  className="mt-1 w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="smtp-submit"
            className="w-full mt-4 flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 py-2.5 rounded-md font-medium text-sm disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />}
            {busy ? "Verifying credentials…" : "Connect mailbox"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Label({ children, icon: Icon }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono flex items-center gap-1.5">
      {Icon && <Icon className="w-3 h-3" strokeWidth={1.5} />}
      {children}
    </div>
  );
}
